import { ASSESSMENT_STATUSES, NO_SHOW_STATUSES } from 'components/Assessments/constants';
import { ORDER_STATUSES } from '../components/Orders/constants';
import { BADGES_COLOR_TYPES } from '../components/Shared/Badge';
import { RESOURCE_STATUSES } from '../components/Resources/constants';
import {
  BILLING_CLAIMS_STATUSES,
  CLAIM_STATUS_SELECTOR_STATUSES,
  CLAIMS_STATUSES,
} from '../components/Claims/constants';
import { CLAIM_REPORTS_STATUSES } from '../components/ClaimReport/constants';
import {
  CUSTOM_TAGS_STATUSES,
  PATIENTS_CUSTOM_TAGS_COLORS_STATUSES,
  SURVEY_TAGS_STATUSES,
} from '../components/Shared/CustomTags';
import { ALERTS_TYPE, ENTITY_SOURCE_TYPE } from '../components/Alerts/constants';
import { BOT_UPLOADS_STATUSES } from '../components/BotActivities/constants';
import { BHI_STATUSES } from '../components/Programs/constants';
import { GENDERS } from '../components/PopulationHealth/widgets/PatientDrilldownModal/components/PatientQhsTable';

const StatusColor = {
  [ORDER_STATUSES.shipped]: BADGES_COLOR_TYPES.green,
  [ORDER_STATUSES.delayed]: BADGES_COLOR_TYPES.grey,
  [ORDER_STATUSES.processing]: BADGES_COLOR_TYPES.lightBlue,
  [ORDER_STATUSES.onHold]: BADGES_COLOR_TYPES.grey,
  [ORDER_STATUSES.pending]: BADGES_COLOR_TYPES.blue,
  [ORDER_STATUSES.sending]: BADGES_COLOR_TYPES.lightBlue,
  [ORDER_STATUSES.canceled]: BADGES_COLOR_TYPES.grey,
  [ASSESSMENT_STATUSES.pendingReview]: BADGES_COLOR_TYPES.orange,
  [ASSESSMENT_STATUSES.partialProcessed]: BADGES_COLOR_TYPES.orange,
  [ASSESSMENT_STATUSES.processing]: BADGES_COLOR_TYPES.red,
  [ASSESSMENT_STATUSES.completed]: BADGES_COLOR_TYPES.green,
  [ASSESSMENT_STATUSES.inProgress]: BADGES_COLOR_TYPES.lightBlue,
  [ASSESSMENT_STATUSES.invited]: BADGES_COLOR_TYPES.grey,
  [ASSESSMENT_STATUSES.created]: BADGES_COLOR_TYPES.grey,
  [ASSESSMENT_STATUSES.scheduled]: BADGES_COLOR_TYPES.grey,
  [ASSESSMENT_STATUSES.eTransfer]: BADGES_COLOR_TYPES.green2,
  [ASSESSMENT_STATUSES.canceled]: BADGES_COLOR_TYPES.grey,
  // Separate `noShowStatus` column (backend split). `noShow` value
  // overlaps the legacy `ASSESSMENT_STATUSES.noShow` literal — both
  // resolve to the same string `'noShow'`, so the red tone also
  // covers historical rows whose `status` field still carries that
  // value. `none` renders as a quiet grey so the dominant
  // "not flagged" rows do not scream for attention.
  [NO_SHOW_STATUSES.noShow]: BADGES_COLOR_TYPES.red,
  [NO_SHOW_STATUSES.none]: BADGES_COLOR_TYPES.grey,
  [RESOURCE_STATUSES.invited]: BADGES_COLOR_TYPES.grey,
  [RESOURCE_STATUSES.engaged]: BADGES_COLOR_TYPES.lightBlue,
  [RESOURCE_STATUSES.scheduled]: BADGES_COLOR_TYPES.grey,
  [CLAIMS_STATUSES.draft]: BADGES_COLOR_TYPES.grey,
  [CLAIMS_STATUSES.notCreated]: BADGES_COLOR_TYPES.lightBlue,
  [CLAIMS_STATUSES.completed]: BADGES_COLOR_TYPES.green,
  [BILLING_CLAIMS_STATUSES.billed]: BADGES_COLOR_TYPES.grey,
  [BILLING_CLAIMS_STATUSES.icdIssue]: BADGES_COLOR_TYPES.lightBlue,
  [BILLING_CLAIMS_STATUSES.insuranceIssue]: BADGES_COLOR_TYPES.grey,
  [BILLING_CLAIMS_STATUSES.appointmentTypeIssue]: BADGES_COLOR_TYPES.grey,
  [BILLING_CLAIMS_STATUSES.hold]: BADGES_COLOR_TYPES.grey,
  [BILLING_CLAIMS_STATUSES.appointmentCancelled]: BADGES_COLOR_TYPES.blue,
  [CLAIM_REPORTS_STATUSES.reported]: BADGES_COLOR_TYPES.green,
  [CLAIM_REPORTS_STATUSES.reportable]: BADGES_COLOR_TYPES.green2,

  [ALERTS_TYPE.critical]: BADGES_COLOR_TYPES.red,
  [ALERTS_TYPE.nonCritical]: BADGES_COLOR_TYPES.lightGrey2,

  [ENTITY_SOURCE_TYPE.program]: BADGES_COLOR_TYPES.green,
  [ENTITY_SOURCE_TYPE.assessment]: BADGES_COLOR_TYPES.lightBlue,
  [ENTITY_SOURCE_TYPE.resource]: BADGES_COLOR_TYPES.blue,
  [ENTITY_SOURCE_TYPE.intellyChart]: BADGES_COLOR_TYPES.orange,
  [ENTITY_SOURCE_TYPE.bhiWebhook]: BADGES_COLOR_TYPES.purple,

  [BOT_UPLOADS_STATUSES.success]: BADGES_COLOR_TYPES.green,
  [BOT_UPLOADS_STATUSES.error]: BADGES_COLOR_TYPES.red,

  [CLAIM_STATUS_SELECTOR_STATUSES.patientResponsibility]: BADGES_COLOR_TYPES.green,
  [CLAIM_STATUS_SELECTOR_STATUSES.inProcess]: BADGES_COLOR_TYPES.blue,
  [CLAIM_STATUS_SELECTOR_STATUSES.paid]: BADGES_COLOR_TYPES.orange,
  [CLAIM_STATUS_SELECTOR_STATUSES.partiallyPaid]: BADGES_COLOR_TYPES.purple,
  [CLAIM_STATUS_SELECTOR_STATUSES.denied]: BADGES_COLOR_TYPES.grey,
  [CLAIM_STATUS_SELECTOR_STATUSES.notBillable]: BADGES_COLOR_TYPES.lightGrey,
  [CLAIM_STATUS_SELECTOR_STATUSES.resubmitted]: BADGES_COLOR_TYPES.orange,

  [BILLING_CLAIMS_STATUSES.readyToBill]: BADGES_COLOR_TYPES.orange,
  [BILLING_CLAIMS_STATUSES.insuranceIssue]: BADGES_COLOR_TYPES.red,
  [BILLING_CLAIMS_STATUSES.appointmentTypeIssue]: BADGES_COLOR_TYPES.red,
  [BILLING_CLAIMS_STATUSES.icdIssue]: BADGES_COLOR_TYPES.red,
  [BILLING_CLAIMS_STATUSES.appointmentCancelled]: BADGES_COLOR_TYPES.lightGrey,
  [BILLING_CLAIMS_STATUSES.hold]: BADGES_COLOR_TYPES.lightGrey2,

  [GENDERS.female]: BADGES_COLOR_TYPES.pink,
  [GENDERS.male]: BADGES_COLOR_TYPES.blue,

  [BHI_STATUSES.active]: BADGES_COLOR_TYPES.green,
  [BHI_STATUSES.inactive]: BADGES_COLOR_TYPES.grey,
};

const getStatusBadge = status => StatusColor[status];

export default getStatusBadge;

const TagsColor = {
  [CUSTOM_TAGS_STATUSES['ISP']]: BADGES_COLOR_TYPES.green,
  [CUSTOM_TAGS_STATUSES['License']]: BADGES_COLOR_TYPES.lightBlue,
  [CUSTOM_TAGS_STATUSES['Orders Only']]: BADGES_COLOR_TYPES.blue,
  [CUSTOM_TAGS_STATUSES['Trial']]: BADGES_COLOR_TYPES.orange,
  [CUSTOM_TAGS_STATUSES['Demo']]: BADGES_COLOR_TYPES.red,
  [CUSTOM_TAGS_STATUSES['Other']]: BADGES_COLOR_TYPES.green2,
  [CUSTOM_TAGS_STATUSES['Inactive']]: BADGES_COLOR_TYPES.purple,

  [SURVEY_TAGS_STATUSES['hasClaim']]: BADGES_COLOR_TYPES.orange,
  [SURVEY_TAGS_STATUSES['hasProgram']]: BADGES_COLOR_TYPES.blue,
  [SURVEY_TAGS_STATUSES['hasIntellyChart']]: BADGES_COLOR_TYPES.green,
  [SURVEY_TAGS_STATUSES['instant']]: BADGES_COLOR_TYPES.purple,

  [PATIENTS_CUSTOM_TAGS_COLORS_STATUSES.control]: BADGES_COLOR_TYPES.orange,
  [PATIENTS_CUSTOM_TAGS_COLORS_STATUSES.experimental]: BADGES_COLOR_TYPES.blue,
};

export const getTagsColor = status => TagsColor[status];
